import { useEffect, useRef, useState, type MouseEvent } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Popover, Stack, Typography } from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import useConfig from 'hooks/useConfig';

// assets
import { IconLogout, IconSettings } from '@tabler/icons-react';
import { useAppSelector } from '@/hooks';
import { logoutSession } from '@/services/session-logout';
// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const prevOpen = useRef(open);
    const { borderRadius } = useConfig();
    const { profile } = useAppSelector(({ profile: profileState }) => profileState);
    const anchorRef = useRef<HTMLDivElement>(null);

    const handleLogout = () => {
        void logoutSession();
    };

    const handleToggle = () => {
        setOpen((prev) => !prev);
    };
    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current?.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Box
                ref={anchorRef}
                onClick={handleToggle}
                role="button"
                tabIndex={0}
                aria-controls={open ? 'profile-menu-grow' : undefined}
                aria-haspopup="true"
                sx={{
                    height: '48px',
                    px: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid',
                    borderRadius: '27px',
                    cursor: 'pointer',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    '& .profile-trigger-icon': {
                        color: theme.palette.primary.main
                    },
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.primary.main,
                        '& .profile-trigger-icon': {
                            color: theme.palette.common.white
                        }
                    }
                }}
            >
                <Avatar
                    src={profile?.profileImageUrl ?? ''}
                    sx={{
                        ...theme.typography.mediumAvatar,
                        margin: '8px 0',
                        cursor: 'pointer'
                    }}
                    color="inherit"
                />
                <IconSettings className="profile-trigger-icon" stroke={1.5} size="1.5rem" />
            </Box>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                slotProps={{
                    paper: {
                        onClick: (event: MouseEvent) => event.stopPropagation(),
                        sx: { mt: 1.75, borderRadius: 1, overflow: 'hidden' }
                    }
                }}
            >
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                    <Box sx={{ p: 2, pb: 0 }}>
                        <Stack>
                            <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                                <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                    {profile?.name}
                                </Typography>
                            </Stack>
                            <Typography variant="subtitle2">{profile?.email}</Typography>
                        </Stack>

                        <Divider />
                    </Box>
                    <Box sx={{ p: 2, pt: 0, maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', overflowX: 'hidden' }}>
                        <List
                            component="nav"
                            sx={{
                                width: '100%',
                                maxWidth: 350,
                                minWidth: 300,
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: '10px',
                                [theme.breakpoints.down('md')]: {
                                    minWidth: '100%'
                                },
                                '& .MuiListItemButton-root': {
                                    mt: 0.5
                                }
                            }}
                        >
                            <ListItemButton sx={{ borderRadius: `${borderRadius}px` }} onClick={handleLogout}>
                                <ListItemIcon>
                                    <IconLogout stroke={1.5} size="1.3rem" />
                                </ListItemIcon>
                                <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                            </ListItemButton>
                        </List>
                    </Box>
                </MainCard>
            </Popover>
        </>
    );
};

export default ProfileSection;
